import {Button} from "@traas/lib/src/components/form/Button";
import {faChevronRight, faPlus, faWarning} from "@fortawesome/free-solid-svg-icons";
import {Toggle} from "@traas/lib/src/components/form/Toggle";
import {Input} from "@traas/lib/src/components/form/Input";
import React, {FC, useMemo, useState} from "react";
import {EmployeeAvatar} from "./EmployeeAvatar";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useApi} from "../../api/APIContext";
import {useModal} from "@traas/lib/src/components/layout/ModalProvider";
import {AddEmployeeModal} from "../../modals/AddEmployeeModal";
import {usePermissions} from "../../auth/permissions";
import {Employee} from "@traas/lib/src/api/dto/Employee";
import {useNavigate} from "react-router";

export const EmployeeManager = () => {
    const {employees} = useApi()
    const permissions = usePermissions()
    const [filter, setFilter] = useState('')
    const [onlyActive, setOnlyActive] = useState(true)
    const [expanded, setExpanded] = useState<string|null>(null)
    const filteredEmployees = useMemo(() => {
        return employees.filter(e => `${e.firstName},${e.lastName},${e.email??''}`.toLowerCase().includes(filter))
            .filter(e => onlyActive ? e.enabled : true)
    }, [employees, filter, onlyActive])
    const addEmployeeModal = useModal({title: 'Nieuw personeel', body: <AddEmployeeModal />})
    return <>
        <div className={"flex justify-between items-center h-12 mb-4"}>
            <h1 className={"flex items-center text-2xl font-bold"}>Personeel</h1>
          {permissions.canAddEmployees &&
            <Button icon={faPlus} onClick={() => addEmployeeModal.open()} text={'Nieuw personeel'} type={'primary'} size={'md'} />}
        </div>

        <div className={"flex justify-between items-center mb-4"}>
            <label className={`h-10 px-4 flex cursor-pointer items-center space-x-3 ${onlyActive ? 'bg-brand-100' : ''} rounded-lg font-medium`}>
                <Toggle value={onlyActive} onChange={setOnlyActive} />
                <span>Alleen actief personeel</span>
            </label>
            <div className={"max-w-(--breakpoint-sm)"}>
                <Input value={filter} onChange={setFilter} type={'text'} placeholder={'Zoeken...'} label={''} />
            </div>
        </div>

      <div className={'bg-white border border-slate-100 rounded-sm'}>
        {filteredEmployees.length === 0 && <div className={"text-center my-8 font-medium text-slate-600"}>Geen resultaten</div>}
        {filteredEmployees.map((e, i) =>
          <EmployeeRowCard employee={e} isFirst={i === 0} isLast={filteredEmployees.length - 1 === i} key={i} />
        )}
      </div>
    </>
}

const EmployeeRowCard: FC<{ employee: Employee, isFirst: boolean, isLast: boolean }> = ({ employee, isFirst, isLast }) => {
  const navigate = useNavigate()
  return <div className={`flex flex-row py-1 border ${isFirst ? 'rounded-t' : ''} ${isLast ? 'rounded-b' : ''} hover:bg-brand-50 hover:cursor-pointer`} onClick={() => navigate(`/personeel/${employee.id}`)}>
    {/*Employee Avatar*/}
    <div className={"flex items-center justify-center mr-5"}>
      <EmployeeAvatar employee={employee} />
    </div>

    {/*Row Content*/}
    <div className={'flex flex-row flex-1 items-center'}>

      <div className={'w-full grid grid-cols-10'}>
        {/* Naam + red notifs */}
        <div className={"col-span-5 flex flex-col space-y-1 "}>
          <div className={'font-medium'}>{employee.firstName} {employee.lastName}</div>
          {!employee.metadata.isBsnSet || employee.metadata.contractStartDate === null ?
            <div className={'flex space-x-3 mt-1'}>
              {!employee.metadata.isBsnSet && <span className={"px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs"}>
              <FontAwesomeIcon icon={faWarning} className={'mr-1'}/>BSN niet ingevuld
            </span>}
              {employee.metadata.contractStartDate === null && <span className={"px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs"}>
              <FontAwesomeIcon icon={faWarning} className={'mr-1'}/>
              Startdatum contract onbekend</span>
              }
            </div>
            : <></>
          }
        </div>

        {/* Login permissions */}
        <div className={'col-span-2 flex items-center'}>
          {employee.email ? <span className={"px-2 py-1 bg-brand-200 text-brand-600 rounded-full text-xs"}>Kan inloggen</span>
            : <span className={"px-2 py-1 text-slate-600 text-xs"}>Kan niet inloggen</span>}
        </div>

        {/* Roles */}
        <div className={'col-span-3 flex items-center'}>
          {employee.roles.map((role, j) =>
              <span key={j} className={"text-xs mx-1 uppercase tracking-wide inline-block px-1 rounded-sm border border-brand-300 text-brand-400"}>{role}</span>
          )}

          {employee.metadata.shadowEmployee
            && <span className={"text-xs mx-1 uppercase tracking-wide inline-block px-1 rounded-sm border border-brand-300 text-brand-400"}>Schaduw</span>
          }

          <span className={`text-xs mx-1 uppercase tracking-wide inline-block px-1 rounded-sm border ${employee.enabled ? 'border-brand-300 text-brand-400' : 'border-red-300 text-red-400'}`}>{employee.enabled ? "Actief" : "Non-actief"}</span>
        </div>
      </div>

      {/*Detail navigation*/}
      <div className={"flex flex-row items-center justify-center space-x-1 px-3 text-brand-900"}>
        <div>Details</div>
        <FontAwesomeIcon icon={faChevronRight} />
      </div>
    </div>
  </div>
}
